import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getLogger } from "@/infrastructure/di";
import { cacheBuilder, restore, type CacheBuilder, type CacheEffect } from "./cache/cacheEffects";
import type { CacheEntry } from "./cache/cacheSlots";
import { keysToSettle } from "./cache/settleQueue";

interface CreateMutationConfig<TData, TError, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** What the write does to the cache — required, so no mutation can forget it. */
  cache: (builder: CacheBuilder<TData, TVariables>) => CacheEffect<TData, TVariables>;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  errorLogMessage?: string;
}

interface MutationContext {
  snapshot: readonly CacheEntry[];
}

/**
 * Hook factory for every mutation: the declared cache effect paints the
 * optimistic state, writes the server response and reconciles the rest in the
 * background — invalidations are never awaited, so `mutateAsync` resolves as
 * soon as the server answers.
 * Internal to application/queries — not exported from the barrel.
 */
export function useCreateMutation<TData = unknown, TError = Error, TVariables = void>(
  config: CreateMutationConfig<TData, TError, TVariables>,
) {
  const queryClient = useQueryClient();
  const effect = config.cache(cacheBuilder<TData, TVariables>());
  const scopeId = effect.scope;
  const scopeBusy = () =>
    scopeId !== undefined &&
    queryClient.isMutating({ predicate: (mutation) => mutation.options.scope?.id === scopeId }) > 1;

  return useMutation<TData, TError, TVariables, MutationContext>({
    mutationFn: config.mutationFn,
    mutationKey: effect.group === undefined ? undefined : [effect.group],
    scope: scopeId === undefined ? undefined : { id: scopeId },
    onMutate: async (variables) => ({ snapshot: await effect.prepare(queryClient, variables) }),
    onSuccess: (data, variables, context) => {
      if (!scopeBusy()) effect.commit(queryClient, data, variables, context.snapshot);
      config.onSuccess?.(data, variables);
    },
    onError: (error, variables, context) => {
      if (context) restore(queryClient, context.snapshot);
      getLogger().error(config.errorLogMessage ?? "Mutation failed:", error);
      config.onError?.(error, variables);
    },
    onSettled: (data, _error, variables) => {
      const keys = keysToSettle(queryClient, effect.group, effect.settleKeys(data, variables));
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    },
  });
}
